import CloseIcon from "components/svgs/CloseIcon";
import React, { useState, useRef } from "react";
import Popup from "reactjs-popup";
import ReactCrop from "react-image-crop";
import { useDebounceEffect } from "hooks/useDebounceEffect";

const TO_RADIANS = Math.PI / 180;

const canvasPreview = async (image, canvas, crop, scale = 1, rotate = 0) => {
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("No 2d context");
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = "high";

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = rotate * TO_RADIANS;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();

  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );

  ctx.restore();
};

const CropImagePopup = ({
  showPopup,
  setShowPopup,
  selectedImage,
  setSelectedImage,
}) => {
  const [imgSrc, setImgSrc] = useState(URL.createObjectURL(selectedImage.file));
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const blobUrlRef = useRef("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedImage(null);
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  const handleSaveClick = async () => {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error("Crop canvas does not exist");
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const offscreen = new OffscreenCanvas(
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );
    const ctx = offscreen.getContext("2d");
    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );
    // You might want { type: "image/jpeg", quality: <0 to 1> } to
    // reduce image size
    const blob = await offscreen.convertToBlob({
      type: "image/png",
    });

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    blobUrlRef.current = URL.createObjectURL(blob);
    handleClosePopup();
    setSelectedImage({
      ...selectedImage,
      file: new File([blob], `cropped-image-${Date.now()}.png`, {
        type: "image/png",
      }),
    });
  };

  return (
    <Popup
      open={showPopup}
      onClose={handleClosePopup}
      closeOnDocumentClick={false}
      className="crop-image-popup"
    >
      <div className="close-crop-image-popup" onClick={handleClosePopup}>
        <CloseIcon />
      </div>
      <div className="crop-image-popup-container">
        {!!imgSrc && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            minHeight={100}
          >
            <img ref={imgRef} alt="Crop me" src={imgSrc} />
          </ReactCrop>
        )}
        {!!completedCrop && (
          <div className="crop-image-preview-container">
            <canvas
              ref={previewCanvasRef}
              style={{
                border: "1px solid black",
                objectFit: "contain",
                width: completedCrop.width,
                height: completedCrop.height,
              }}
            />
          </div>
        )}
      </div>
      <div className="crop-image-save">
        <button disabled={!completedCrop} onClick={handleSaveClick}>
          Save
        </button>
      </div>
    </Popup>
  );
};

export default CropImagePopup;
